/**
 * THE RENTALS CARD ON AN RV'S OWN PAGE.
 *
 * Anthony, 2026-09-28, for WeRV. A unit in the rental fleet is the same
 * vehicles row the repair side uses, so its rental terms live on the page
 * that row already has, not on a second screen about the same RV. Shown
 * only to a shop with a rentals side (hasRentals in src/domain/side.ts);
 * every other trade's vehicle page is exactly what it was.
 *
 * SETTING A NIGHTLY RATE IS WHAT PUTS A UNIT ON THE FLEET. The calendar
 * and the New trip form offer every unit with a rate, and "Renting it out"
 * takes one off without losing its numbers. An RV yard that also repairs
 * RVs has customers' RVs in the same table, and those simply never get a
 * rate.
 *
 * THE FLEET OWNER. Some units belong to a partner in the fleet owner
 * program. Their name and percent go here, and every trip on the unit
 * shows the owner's share of the trip (the yard's prep, delivery and setup
 * fees stay with the yard).
 *
 * Read with the signed-in person's own row security; written through
 * /api/rentals/units/:id, which checks who is asking and scopes to their
 * shop. Columns from migration 0185: before it is applied the read fails
 * with an unknown column, and the card says the rentals side is not
 * switched on rather than showing a database error.
 */

import { db } from "../data/client.ts";
import { rentalsApi } from "../data/rentals.ts";
import { esc } from "../ui/dom.ts";
import { UNIT_SELECT, type Unit } from "../domain/rental.ts";

export async function rentalCard(hostId: string, vehicleId: string, mayWrite: boolean): Promise<void> {
  const host = document.getElementById(hostId);
  if (!host) return;
  const sb = await db();
  const { data, error } = await sb.from("vehicles").select(UNIT_SELECT).eq("id", vehicleId).maybeSingle();
  if (error || !data) {
    host.innerHTML = `<div class="card"><div class="card-head">Rentals</div><div class="empty">${
      error && /column|42703/i.test(error.message) ? "The rentals side is not switched on for this shop yet."
        : esc(error?.message ?? "Could not read this RV.")}</div></div>`;
    return;
  }
  const u = data as unknown as Unit;
  const dis = mayWrite ? "" : " disabled";
  const num = (id: string, label: string, v: number | null, step = "1") =>
    `<label class="field"><span>${label}</span><input id="${id}" type="number" min="0" step="${step}" value="${esc(v ?? "")}"${dis}></label>`;
  host.innerHTML = `<div class="card"><div class="card-head">Rentals</div><div class="pad">
      <p class="quiet">${u.rental_nightly_rate !== null
        ? (u.rental_active === false ? "On the fleet, but not being rented out right now." : "On the rental fleet. It shows on the calendar and in New trip.")
        : "Not on the rental fleet. Give it a nightly rate to rent it out."}</p>
      <div class="rtwo">${num("rf-rate", "Nightly rate", u.rental_nightly_rate, "0.01")}${num("rf-dep", "Deposit hold", u.rental_deposit, "0.01")}</div>
      <div class="rtwo">${num("rf-prep", "Prep fee", u.rental_prep_fee, "0.01")}${num("rf-min", "Minimum nights", u.rental_min_nights)}</div>
      <div class="rtwo">${num("rf-age", "Minimum driver age", u.rental_min_age)}${num("rf-sleeps", "Sleeps", u.rental_sleeps)}</div>
      <label class="check"><input type="checkbox" id="rf-active"${u.rental_active !== false ? " checked" : ""}${dis}> Renting it out</label>
      <div class="rtwo"><label class="field"><span>Fleet owner, if a partner owns it</span>
          <input id="rf-owner" type="text" value="${esc(u.fleet_owner_name ?? "")}"${dis}></label>
        ${num("rf-pct", "Owner's share, percent", u.fleet_owner_payout_pct, "0.01")}</div>
      ${mayWrite ? `<button class="btn primary sm" id="rf-save">Save the rental terms</button><p class="msg" id="rf-msg" hidden></p>` : ""}
    </div></div>`;

  document.getElementById("rf-save")?.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    const v = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? "";
    b.disabled = true;
    const r = await rentalsApi<{ unit: Unit }>(`/units/${encodeURIComponent(vehicleId)}`, "PATCH", {
      rental_nightly_rate: v("rf-rate"), rental_deposit: v("rf-dep"), rental_prep_fee: v("rf-prep"),
      rental_min_nights: v("rf-min"), rental_min_age: v("rf-age"), rental_sleeps: v("rf-sleeps"),
      rental_active: (document.getElementById("rf-active") as HTMLInputElement | null)?.checked !== false,
      fleet_owner_name: v("rf-owner"), fleet_owner_payout_pct: v("rf-pct"),
    });
    b.disabled = false;
    const m = document.getElementById("rf-msg");
    if (m) {
      m.hidden = false;
      m.className = `msg ${r.ok ? "good" : "bad"}`;
      m.textContent = r.ok ? "Saved." : r.error;
    }
  });
}
